import { LoadingState } from "@/ds/custom/LoadingState";
import { useImportPreflight } from "@/features/import/api/useImportPreflight";
import { useImportUpload } from "@/features/import/api/useImportUpload";
import { FileSelector } from "@/features/import/components/FileSelector";
import { useNotifyMutationError } from "@/lib/common/useNotifyMutationError";
import { useEffect, useState } from "react";
import csvIcon from "@/assets/csv.png";
import { Button } from "@/ds/shadcn/components/ui/button";
import { Link } from "react-router";

export interface ImportUploadProps {
	collectionId: string | undefined;
}

export const ImportUpload = ({ collectionId }: ImportUploadProps): React.JSX.Element => {
	const importPreflight = useImportPreflight();
	useNotifyMutationError(importPreflight);
	const importUpload = useImportUpload();
	useNotifyMutationError(importUpload);
	const [file, setFile] = useState<File | null>(null);

	useEffect(() => {
		if (!file || importPreflight.isPending) {
			return;
		}

		getHeadersLineOfFile(file, headers => {
			if (!headers) {
				setFile(null);
				return;
			}

			importPreflight.mutate({
				collectionId,
				requestModel: {
					headers
				}
			});
		});
	}, [file]);

	useEffect(() => {
		if (!importPreflight.data || !file) {
			return;
		}

		importUpload.mutate({
			csvImportToken: importPreflight.data.body.csvImportToken,
			file: file
		});
	}, [importPreflight.data]);

	useEffect(() => {
		if (!importPreflight.error && !importUpload.error) {
			return;
		}

		setFile(null);
	}, [importPreflight.error, importUpload.error]);

	return (
		<>
			<div className="flex flex-col justify-center bg-background-highlight p-12 cursor-pointer">
				<FileSelector file={file} onFileSelected={setFile} accept="text/csv" />

				{
					(importPreflight.isPending || importUpload.isPending) &&
					<LoadingState />
				}
			</div>

			<div className="type-description p-3 border-b">
				The easiest way to get started with CSV Import is to download our CSV template file below. It contains a row for every product we support with a quantity of 0. You can read a more in-depth explanation of our format by <a className="text-primary underline-offset-4 hover:underline" target="_blank" rel="noreferrer" href="/collections/csv-tutorial">clicking here</a>.
			</div>

			<div className="flex justify-between border-b items-center gap-3 p-3">
				<div className="flex items-center gap-2">
					<img className="w-8" src={csvIcon} />

					<div>
						<div className="type-item">csv-import-template.csv</div>
						<div className="type-meta">672 kB</div>
					</div>
				</div>

				<Link rel="noreferrer" target="_blank" to="https://cdn.fyendalscollection.com/csv-import-template.csv">
					<Button variant="outline">
						Download
					</Button>
				</Link>
			</div>
		</>
	);
};

const getHeadersLineOfFile = (file: File, callback: (headers: string | null) => void) => {
	const reader = new FileReader();

	reader.addEventListener("load", e => {
		if (!e.target || typeof e.target.result !== "string") {
			return;
		}

		const lines = e.target.result.split("\n");
		callback(lines[0]);
	});

	reader.addEventListener("error", () => callback(null));
	reader.readAsText(file);
};
